import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useFormik } from 'formik'
import { Camera } from 'lucide-react'
import React, { useRef, useState, useMemo } from 'react'
import { toast } from 'sonner'
import * as yup from 'yup'
import ModalContainer from '../../../../../components/modals/modal-container'

interface BusinessType {
  id: string | number
  name: string
  description?: string
  image_url?: string
  position?: number
  is_published: boolean
  vendor_count?: number
}

interface AddBusinessTypeModalProps {
  show: boolean
  handleClose: () => void
  onSubmit?: (data: BusinessTypeFormData) => void
  loading?: boolean
  editingItem?: BusinessType | null
}

interface BusinessTypeFormData {
  name: string
  image?: File
  is_published?: boolean
}

const createBusinessTypeSchema = (hasExistingImage: boolean) =>
  yup.object().shape({
    name: yup
      .string()
      .min(2, 'Business type name must be at least 2 characters')
      .max(50, 'Business type name must be less than 50 characters')
      .required('Business type name is required'),
    image: hasExistingImage
      ? yup
          .mixed<File>()
          .test('fileType', 'Only .jpg and .png formats are allowed', (value) => {
            if (!value) return true // Optional when editing with existing image
            if (value instanceof File) {
              return ['image/jpeg', 'image/jpg', 'image/png'].includes(value.type)
            }
            return true
          })
          .test('fileSize', 'Image size must be less than 1MB', (value) => {
            if (!value) return true // Optional when editing with existing image
            if (value instanceof File) {
              return value.size <= 1024 * 1024 // 1MB
            }
            return true
          })
      : yup
          .mixed<File>()
          .required('Business image is required')
          .test('fileType', 'Only .jpg and .png formats are allowed', (value) => {
            if (!value) return false
            if (value instanceof File) {
              return ['image/jpeg', 'image/jpg', 'image/png'].includes(value.type)
            }
            return true
          })
          .test('fileSize', 'Image size must be less than 1MB', (value) => {
            if (!value) return false
            if (value instanceof File) {
              return value.size <= 1024 * 1024 // 1MB
            }
            return true
          }),
  })

const AddBusinessTypeModal = ({
  show,
  handleClose,
  onSubmit,
  loading,
  editingItem,
}: AddBusinessTypeModalProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const selectedImageRef = useRef<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(
    editingItem?.image_url || null
  )
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [hasSubmitted, setHasSubmitted] = useState(false)

  const validationSchema = useMemo(
    () => createBusinessTypeSchema(!!editingItem?.image_url),
    [editingItem?.image_url]
  )

  const {
    values,
    handleChange,
    touched,
    errors,
    isValid,
    dirty,
    resetForm,
    setFieldTouched,
    setValues,
    setFieldValue,
  } = useFormik({
    initialValues: {
      name: editingItem?.name || '',
      image: undefined as File | undefined,
      is_published: editingItem?.is_published ?? false,
    },
    validationSchema,
    onSubmit: (values) => {
      onSubmit?.(values)
    },
    enableReinitialize: true,
  })

  React.useEffect(() => {
    if (hasSubmitted && loading) {
      return
    }

    if (editingItem) {
      setValues({
        name: editingItem.name,
        image: undefined,
        is_published: editingItem.is_published ?? false,
      })
      setImagePreview(editingItem.image_url || null)
      if (!hasSubmitted) {
        setSelectedImage(null)
        selectedImageRef.current = null
      }
    } else {
      setValues({ name: '', image: undefined, is_published: false })
      setImagePreview(null)
      if (!hasSubmitted) {
        setSelectedImage(null)
        selectedImageRef.current = null
      }
    }
    if (!hasSubmitted) {
      setHasSubmitted(false)
    }
  }, [editingItem, setValues, hasSubmitted, loading])

  React.useEffect(() => {
    if (hasSubmitted && !loading && !show) {
      resetForm()
      if (!editingItem) {
        setImagePreview(null)
      } else {
        setImagePreview(editingItem?.image_url || null)
      }
      setSelectedImage(null)
      selectedImageRef.current = null
      setHasSubmitted(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }, [hasSubmitted, loading, show, editingItem, resetForm])

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Check file type
      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
        toast.error('Only .jpg and .png formats are allowed')
        return
      }
      // Check file size (1MB)
      if (file.size > 1024 * 1024) {
        toast.error('Image size must be less than 1MB')
        return
      }
      setSelectedImage(file)
      selectedImageRef.current = file
      setFieldValue('image', file)
      setFieldTouched('image', true)
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const file = e.dataTransfer.files?.[0]
    if (file) {
      // Check file type
      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
        toast.error('Only .jpg and .png formats are allowed')
        return
      }
      // Check file size (1MB)
      if (file.size > 1024 * 1024) {
        toast.error('Image size must be less than 1MB')
        return
      }
      setSelectedImage(file)
      selectedImageRef.current = file
      setFieldValue('image', file)
      setFieldTouched('image', true)
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }

  const handleCloseModal = () => {
    resetForm()
    setImagePreview(editingItem?.image_url || null)
    setSelectedImage(null)
    selectedImageRef.current = null
    setHasSubmitted(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    handleClose()
  }

  return (
    <ModalContainer
      show={show}
      handleClose={handleCloseModal}
      modalClass='rounded-3xl py-6 sm:py-10 px-4 sm:px-6 bg-white w-full max-w-[95vw] sm:max-w-[28.8rem] md:w-[28.8rem] md:max-w-[30.8rem] lg:w-[28rem] mx-4 sm:mx-0 max-h-[95vh] flex flex-col overflow-hidden'
      hasCloseButton={true}
    >
      <section className='w-full flex flex-col flex-1 min-h-0'>
        <div className='text-center sm:text-left mb-6 -mt-2 sm:-mt-6 flex-shrink-0'>
          <h3 className='text-base sm:text-lg font-bold text-gray-900 mb-2'>
            {editingItem ? 'Edit Business Type' : 'Add New Business Type'}
          </h3>
          <p className='text-xs sm:text-sm text-gray-500 leading-relaxed'>
            {editingItem
              ? 'Update the business type that vendors can use when creating their business profiles.'
              : 'Add a new business type that vendors can use.'}
          </p>
        </div>

        <form
          onSubmit={(e) => e.preventDefault()}
          className='w-full flex flex-col flex-1 min-h-0'
        >
          <div className='space-y-4 flex-1 overflow-y-auto pr-2 custom-scroll'>
            <div>
              <label
                htmlFor='name'
                className='block text-sm font-semibold text-gray-700 mb-2'
              >
                Name
              </label>
              <div className='w-full'>
                <Input
                  id='name'
                  name='name'
                  type='text'
                  placeholder='e.g bakery'
                  value={values.name}
                  onChange={handleChange}
                  onBlur={() => setFieldTouched('name', true)}
                  className={cn(
                    'w-full rounded-[1rem]',
                    touched.name &&
                      errors.name &&
                      'border-red-500 focus:border-red-500'
                  )}
                  error={errors.name}
                  touched={touched.name}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor='image-upload'
                className='block text-sm font-semibold text-gray-700 mb-2'
              >
                Upload business Image
                {!editingItem?.image_url && (
                  <span className='text-red-500'>*</span>
                )}
              </label>
              <div className='w-full mb-2'>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  className={cn(
                    'flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors relative',
                    touched.image &&
                      errors.image &&
                      'border-red-500 focus:border-red-500'
                  )}
                >
                  {imagePreview ? (
                    <div className='absolute inset-0 w-full h-full'>
                      <img
                        src={imagePreview}
                        alt='Preview'
                        className='w-full h-full object-cover rounded-lg'
                      />
                    </div>
                  ) : (
                    <div className='flex flex-col items-center justify-center pt-5 pb-6'>
                      <div className='w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center mb-2'>
                        <Camera className='w-5 h-5 text-gray-400' />
                      </div>
                      <p className='mb-1 text-sm text-gray-600 font-medium'>
                        Upload business image
                      </p>
                      <p className='text-xs text-gray-500'>
                        Allowed formats .jpg & .png. less than 1mb
                      </p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    id='image-upload'
                    type='file'
                    className='hidden'
                    accept='image/jpeg,image/jpg,image/png'
                    onChange={handleImageChange}
                  />
                </div>
              </div>
              {touched.image && errors.image && (
                <p className='text-xs text-red-500 mt-1'>{errors.image}</p>
              )}
            </div>
          </div>

          <div className='mt-6 sm:mt-8 flex flex-col sm:flex-row justify-center sm:justify-center gap-3 sm:space-x-3 flex-shrink-0 pt-4 border-t border-gray-100'>
            <Button
              type='button'
              variant='ghost'
              onClick={handleCloseModal}
              className='px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl font-normal w-full sm:w-auto order-2 sm:order-1'
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              onClick={() => {
                setFieldTouched('name', true)
                if (!editingItem?.image_url) {
                  setFieldTouched('image', true)
                }
                const hasImage = selectedImage || imagePreview || editingItem?.image_url
                if (
                  isValid &&
                  (dirty || selectedImage || (editingItem ? hasImage : imagePreview))
                ) {
                  const imageToSubmit =
                    selectedImage || selectedImageRef.current || values.image
                  const submitData = {
                    ...values,
                    image: imageToSubmit,
                  }
                  setHasSubmitted(true)
                  onSubmit?.(submitData)
                }
              }}
              disabled={
                !isValid ||
                (!dirty &&
                  !selectedImage &&
                  !imagePreview &&
                  !editingItem?.image_url) ||
                loading
              }
              loadingText={editingItem ? 'Updating...' : 'Adding Business Type...'}
              loading={loading}
              className='px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl font-normal bg-primary hover:bg-primary/90 text-primary-foreground w-full order-1 sm:order-2'
            >
              {editingItem ? 'Update Business Type' : 'Add Business Type'}
            </Button>
          </div>
        </form>
      </section>
    </ModalContainer>
  )
}

export default AddBusinessTypeModal
